import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Stack,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Alert,
  Box,
  IconButton,
} from "@mui/material";
import { subDays } from "date-fns";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment";
import actions from "actions";
import ListItemSection from "../../Widgets/ListItemSection";
import NewAuthorizationDialog from "./NewAuthorizationDialog";
import UpdateAuthorizationDialog from "./UpdateAuthorizationDialog";
import ViewAuthorizationDialog from "./ViewAuthorizationDialog";
import useOverlapDetection from "./useOverlapDetection";
import useNewAuthorization from "./useNewAuthorization";
import useUpdateAuthorization from "./useUpdateAuthorization";
import { PencilSimpleLine } from "@phosphor-icons/react/dist/ssr";

interface EditFieldsProps {
  clientId: string;
  authorizationCodes: any[];
  availableInsuranceCodes: any[];
  clinicianList: any[];
  userPermissions: any;
  silnaAssessmentAuthStatus: string;
  selectedTab: "ongoing" | "secondary";
}

const EditFields: React.FC<EditFieldsProps> = ({
  clientId,
  authorizationCodes,
  availableInsuranceCodes,
  clinicianList,
  userPermissions,
  silnaAssessmentAuthStatus,
  selectedTab,
}) => {
  const dispatch = useDispatch();

  const [viewMore, setViewMore] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState<any>({});

  const overlap = useOverlapDetection(authorizationCodes);

  const newAuth = useNewAuthorization({
    clientId,
    availableInsuranceCodes,
    overlapError: overlap.overlapError,
    onOverlapDetected: () => overlap.setOverlapDialogOpen(true),
    checkForOverlaps: overlap.checkForOverlaps,
    resetOverlap: overlap.resetOverlap,
  });

  const updateAuth = useUpdateAuthorization({
    clientId,
    authorizationCodes,
    availableInsuranceCodes,
    overlapError: overlap.overlapError,
    onOverlapDetected: () => overlap.setOverlapDialogOpen(true),
    checkForOverlaps: overlap.checkForOverlaps,
    resetOverlap: overlap.resetOverlap,
  });

  useEffect(() => {
    dispatch(actions.setCliniciansPerPage(999));
    dispatch(actions.setCliniciansSort({ sort: "first_name" }));
    dispatch(actions.loadCliniciansList());
  }, [dispatch]);

  useEffect(() => {
    if ((updateAuth.open || newAuth.open) && (!clinicianList || clinicianList?.length === 0)) {
      dispatch(actions.setCliniciansPerPage(999));
      dispatch(actions.setCliniciansSort({ sort: "first_name" }));
      dispatch(actions.loadCliniciansList());
    }
  }, [updateAuth.open, newAuth.open, clinicianList, dispatch]);

  const mapAuthTypeToDisplay = (authType: string) => {
    if (!authType) return "----";
    if (authType === "pre_auth") return "Pre-Authorization";
    if (authType === "auth") return "Ongoing Authorization";
    if (authType === "re_auth") return "Re-Authorization";
    return "Secondary Authorization";
  };

  const renderListSubtitle = (code: any) =>
    `${moment(code.start_date).utc().format("MM/DD/YYYY")} - ${moment(code.end_date).utc().format("MM/DD/YYYY")} | ${code.authorization_code} ${code.clinician_name ? `| ${code.clinician_name}` : ""}`;

  const submitAuthWithOverlap = () => {
    overlap.setOverlapDialogOpen(false);

    const authType = newAuth.open ? newAuth.authType : updateAuth.authType;
    const authStartDate = newAuth.open ? newAuth.startDate : updateAuth.startDate;

    for (let i = 0; i < overlap.overlappingAuths.length; i++) {
      const auth = overlap.overlappingAuths[i];
      const isSecondaryAuth = auth.authorization_type === "secondary_auth";

      dispatch(
        actions.updateAuthorizationCode({
          isSecondaryAuth,
          authId: auth.id,
          authorizationCode: auth.authorization_code,
          startDate: auth.start_date,
          endDate: subDays(new Date(authStartDate!), 1),
          clientId: auth.client_id,
          authorizationType: auth.authorization_type || "",
          clinicianId: auth.clinician_id,
          pendingAuth: auth.pending_auth,
          approvedUnits: isSecondaryAuth
            ? null
            : auth.approvedUnits?.map(
              ({
                approved_time: hours,
                approved_units: units,
                code,
                id: unitId,
                insurance_code_id: codeId,
                client_id: clientIdVal,
              }: any) => ({
                hours,
                units,
                code,
                unitId,
                codeId,
                clientId: clientIdVal,
              })
            ),
        })
      );
    }

    const isSecondaryAuth = authType === "secondary_auth";

    if (newAuth.open) {
      newAuth.submit(isSecondaryAuth);
    } else {
      updateAuth.submit(isSecondaryAuth);
    }

    overlap.resetOverlap();
  };

  const isSecondaryTab = selectedTab === "secondary";

  const filteredCodes = authorizationCodes
    .map((code, index) => ({ ...code, originalIndex: index }))
    .filter((code) =>
      isSecondaryTab
        ? code.authorization_type === "secondary_auth"
        : code.authorization_type !== "secondary_auth"
    );

  const displayedCodes = viewMore ? filteredCodes : filteredCodes.slice(0, 3);
  const additional = filteredCodes.length - displayedCodes.length;

  const activeAuthType = newAuth.open ? newAuth.authType : updateAuth.authType;
  const activeApprovedUnits = newAuth.open
    ? newAuth.approvedUnits
    : [...updateAuth.approvedUnits, ...updateAuth.newApprovedUnits];

  return (
    <>
      <Stack width="100%" gap="12px">
        {authorizationCodes.length > 0 && (
          <>
            {displayedCodes.length > 0 ? (
              displayedCodes.map((code) => (
                <ListItemSection
                  key={code.originalIndex}
                  title={mapAuthTypeToDisplay(code.authorization_type)}
                  subtitle={renderListSubtitle(code)}
                  isPrimary={false}
                  onClick={() => {
                    setViewData(authorizationCodes[code.originalIndex]);
                    setViewOpen(true);
                  }}
                  customEditComponent={
                    <IconButton
                      size="small"
                      fontSize="small"
                      aria-label="Edit authorization"
                      onClick={() => updateAuth.openDialog(code.originalIndex)}
                    >
                      <PencilSimpleLine weight="duotone" />
                    </IconButton>
                  }
                />
              ))
            ) : (
              <Typography variant="body.small" color="text.secondary">
                {isSecondaryTab ? "No secondary authorizations" : "No ongoing authorizations"}
              </Typography>
            )}
            {filteredCodes.length > 3 && (
              <Button
                onClick={() => setViewMore(!viewMore)}
                variant="text"
                sx={{ width: "fit-content" }}
              >
                {viewMore ? "View Less" : `View ${additional} More`}
              </Button>
            )}
          </>
        )}

        {userPermissions?.add_client_insurance_authorization && (
          <Box mt={3}>
            <Button
              color="secondary"
              variant="contained"
              onClick={newAuth.toggle}
              startIcon={<AddIcon />}
            >
              Add Authorization Period
            </Button>
          </Box>
        )}
      </Stack>

      {newAuth.open && (
        <NewAuthorizationDialog
          open={newAuth.open}
          newAuthorizationCode={newAuth.code}
          newAuthorizationStartDate={newAuth.startDate}
          newAuthorizationEndDate={newAuth.endDate}
          newAuthorizationType={newAuth.authType}
          newAuthorizationSubtype={newAuth.subtype}
          newAuthorizationClinicianId={newAuth.clinicianId}
          newPendingAuth={newAuth.pendingAuth}
          newApprovedUnits={newAuth.approvedUnits}
          clinicianList={clinicianList}
          availableInsuranceCodes={availableInsuranceCodes}
          authorizationCodes={authorizationCodes}
          silnaAssessmentAuthStatus={silnaAssessmentAuthStatus}
          onChange={newAuth.handleChange}
          onChangeDate={newAuth.handleDateChange}
          onToggleNewAuthorization={newAuth.toggle}
          saveNewAuthorization={newAuth.save}
          onAddApprovedUnit={newAuth.addApprovedUnit}
          onRemoveApprovedUnit={newAuth.removeApprovedUnit}
          onChangeApprovedUnitCode={newAuth.changeApprovedUnitCode}
          onChangeApprovedUnitAmount={newAuth.changeApprovedUnitAmount}
        />
      )}

      {updateAuth.open && (
        <UpdateAuthorizationDialog
          open={updateAuth.open}
          updateAuthorizationCode={updateAuth.code}
          updateAuthorizationStartDate={updateAuth.startDate}
          updateAuthorizationEndDate={updateAuth.endDate}
          updateAuthorizationType={updateAuth.authType}
          updateAuthorizationClinicianId={updateAuth.clinicianId}
          updatePendingAuth={updateAuth.pendingAuth}
          updateApprovedUnits={updateAuth.approvedUnits}
          updateNewApprovedUnits={updateAuth.newApprovedUnits}
          saveButtonDisabled={updateAuth.saveButtonDisabled}
          availableInsuranceCodes={availableInsuranceCodes}
          clinicianList={clinicianList}
          onCloseUpdateAuthorization={updateAuth.closeDialog}
          onSubmitUpdateAuthorization={updateAuth.save}
          onChange={updateAuth.handleChange}
          onChangeDate={updateAuth.handleDateChange}
          onChangeUpdateApprovedUnitAmount={updateAuth.changeApprovedUnitAmount}
          onAddUpdateNewApprovedUnit={updateAuth.addNewApprovedUnit}
          onRemoveUpdateNewApprovedUnit={updateAuth.removeNewApprovedUnit}
          onChangeUpdateNewApprovedUnitCode={updateAuth.changeNewApprovedUnitCode}
          onChangeUpdateNewApprovedUnitAmount={updateAuth.changeNewApprovedUnitAmount}
        />
      )}

      <ViewAuthorizationDialog
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewData({});
        }}
        data={viewData}
      />

      <Dialog open={overlap.overlapDialogOpen} maxWidth="sm">
        <DialogTitle id="overlap-auth-warning">
          {overlap.getOverlapDialogContent(activeAuthType, activeApprovedUnits).title}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" style={{ margin: "24px 0" }}>
            {overlap.getOverlapDialogContent(activeAuthType, activeApprovedUnits).message}
          </Alert>
          <Box
            width="100%"
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            paddingTop="16px"
          >
            <Button
              onClick={overlap.toggleOverlapDialog}
              style={{ marginRight: "8px" }}
              variant="outlined"
              color="inherit"
            >
              Cancel
            </Button>
            <Button onClick={submitAuthWithOverlap}>
              {newAuth.open ? "Confirm and add authorization period" : "Confirm and Save Changes"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditFields;
